// ==================== Bond Pair Table（元素对键长表）====================
//
// VESTA 风格的「元素对专用 cutoff」。Schomaker–Stevenson 是通用经验公式（共价
// 半径 + 电负性修正），但对部分离子性氧化物（如 LiAlSiO4 的 Si–O 实测 2.10Å、
// Al–O 2.10Å）偏差较大：统一 bondScale 下要么都成键、要么都不成键，无法体现
// VESTA 默认「Si–O 不成键、Al–O 成键」的差异。
//
// 本表提供「按元素对覆盖 cutoff」：查表命中则直接用该 cutoff，否则回退
// Schomaker–Stevenson。优先级：Pair Table > Schomaker。
//
// 扩展：未来 Bond Preset（Generic / Oxide / Organic / Metal Alloy / Custom）可
// 各自提供一套 BondPairRule[]，传给 getBondPairCutoff 的 table 参数即可，无需
// 改 calculateBonds() 主体。当前只实现 DEFAULT_BOND_PAIR_TABLE。

/**
 * 一条元素对键长规则：element1-element2 之间 cutoff Å 以内视为成键。
 * 元素顺序无关（Si-O == O-Si）。
 */
export interface BondPairRule {
  element1: string;
  element2: string;
  cutoff: number;
}

/**
 * 默认元素对键长表（Å）。第一版仅含氧化物常用对，与 VESTA 默认 Bond Search
 * 接近。其余元素对回退 Schomaker–Stevenson。
 *   Si-O 2.00：硅酸盐里 Si-O 典型 ~1.62Å，2.10Å 的过长 Si-O 不成键（VESTA 默认）。
 *   Al-O 2.20：AlO₄ 四面体 Al-O 可达 ~2.1Å，需略宽以完整成键。
 *   Li-O 2.50：Li-O 配位较远（~2.0~2.4Å），需更宽。
 */
export const DEFAULT_BOND_PAIR_TABLE: BondPairRule[] = [
  { element1: 'Si', element2: 'O', cutoff: 2.00 },
  { element1: 'Al', element2: 'O', cutoff: 2.20 },
  { element1: 'Li', element2: 'O', cutoff: 2.50 },
];

/** 把元素对规范化为「字典序较小者-较大者」的 key，使顺序无关。 */
function pairKey(a: string, b: string): string {
  return a <= b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * 查询元素对的专用 cutoff。
 *
 * @param elementA 纯元素符号 A
 * @param elementB 纯元素符号 B
 * @param table    可选，默认 DEFAULT_BOND_PAIR_TABLE。未来 Preset 传各自的表。
 * @returns 命中则返回 cutoff（Å）；未命中返回 undefined（调用方回退 Schomaker）。
 *
 * 顺序无关：getBondPairCutoff('Si','O') === getBondPairCutoff('O','Si') === 2.00。
 */
export function getBondPairCutoff(
  elementA: string,
  elementB: string,
  table: BondPairRule[] = DEFAULT_BOND_PAIR_TABLE
): number | undefined {
  // 表通常很小（几条），线性扫描即可；用 key 比对保证顺序无关。
  const key = pairKey(elementA, elementB);
  for (const rule of table) {
    if (pairKey(rule.element1, rule.element2) === key) {
      return rule.cutoff;
    }
  }
  return undefined;
}
